// Boost Blenz Admin — App section v2: the components the Claude Design handoff introduced
// (SPEC §6), ported onto the live theme tokens. Values, copy and structure are lifted from the
// design prototype; nothing here restyles the existing atoms, it adds to them.
import React from "react";
import { OSWALD, FIGTREE, MONO, useT, useIsMobile } from "../../theme";
import { Icon, Title, Eyebrow, Card, Badge } from "../../atoms";
import { useCachedJson } from "../../datacache";

export { OSWALD, FIGTREE, MONO, useT, Icon, Title, Eyebrow, Card, Badge };

/** One breakpoint for the whole App section (SPEC §4). */
export const useNarrow = () => useIsMobile(860);

/** Detox colours, identical in every chart, calendar and badge (SPEC §4). Real stage lengths. */
export const DETOX: Record<string, { label: string; color: string; days: number }> = {
  liver: { label: "Liver", color: "#ffce5a", days: 14 },
  kidney: { label: "Kidney", color: "#6fe3a0", days: 14 },
  parasite: { label: "Parasite", color: "#b9a9ff", days: 30 },
  metal: { label: "Heavy Metal", color: "#7fd4ff", days: 30 },
};
export const DETOX_ORDER = ["liver", "kidney", "parasite", "metal"];
export const BINDER_COLOR = "#ff8b7d";

export const STATUS_META: Record<string, [string, string]> = {
  detoxing: ["Detoxing", "good"],
  break: ["On a break", "purple"],
  waiting: ["Waiting to start", "warn"],
  finished: ["Finished", "neutral"],
  never: ["Never set up", "bad"],
};

export const TOO_FEW = 10;
export const tooFew = (of: number | null | undefined) => of != null && of < TOO_FEW;

// ------------------------------------------------------------------ dates
export function parseAt(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso.includes("T") ? iso : /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + "T12:00:00Z" : iso.replace(" ", "T") + "Z");
  return Number.isFinite(d.getTime()) ? d : null;
}
export const fmtDay = (iso?: string | null) => parseAt(iso)?.toLocaleDateString(undefined, { month: "short", day: "numeric" }) ?? "—";
export const fmtDayYear = (iso?: string | null) => parseAt(iso)?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) ?? "—";
export const fmtWhen = (iso?: string | null) =>
  parseAt(iso)?.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) ?? "—";
export function relDays(n: number | null | undefined) {
  if (n == null) return "Never";
  return n === 0 ? "Today" : n === 1 ? "Yesterday" : `${n} days ago`;
}

// ------------------------------------------------------------------ data
/** App Health payload, shared by the overview and every drill-down (one fetch, one cache). */
export function useInsights(range: string) {
  const days = range === "7d" ? 7 : range === "90d" ? 90 : range === "all" ? 3650 : 30;
  return useCachedJson<any>(`/api/app/insights?days=${days}`);
}
export function usePeople() {
  return useCachedJson<any>("/api/app/people");
}

// ------------------------------------------------------------------ text
export function Body({ children, size = 13.5, color, style = {} }: any) {
  const t = useT();
  return <div style={{ fontFamily: FIGTREE, fontSize: size, lineHeight: 1.55, color: color || t.fg2, ...style }}>{children}</div>;
}
export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: 999, background: color, display: "inline-block", flexShrink: 0 }} />;
}
export function SectionHead({ title, sub, right, size = 17 }: any) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: sub ? 14 : 12, flexWrap: "wrap" }}>
      <div style={{ minWidth: 0 }}>
        <Title size={size}>{title}</Title>
        {sub && <Body size={12.5} color={useT().muted} style={{ marginTop: 5, maxWidth: 620 }}>{sub}</Body>}
      </div>
      {right && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{right}</div>}
    </div>
  );
}
export function Field({ label, children }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <Eyebrow style={{ fontSize: 10 }}>{label}</Eyebrow>
      <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{children}</div>
    </div>
  );
}
export function Empty({ icon = "spark", title, children, action }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 9, padding: "30px 20px" }}>
      <span style={{ width: 42, height: 42, borderRadius: 999, background: t.surface2, display: "grid", placeItems: "center" }}>
        <Icon name={icon} size={20} color={t.muted} />
      </span>
      <Title size={15}>{title}</Title>
      <Body size={12.5} color={t.muted} style={{ maxWidth: 400 }}>{children}</Body>
      {action}
    </div>
  );
}
export function Note({ children, tone = "neutral", icon = "plug", label, style = {} }: any) {
  const t = useT();
  const col = tone === "bad" ? t.bad : tone === "warn" ? t.warn : tone === "purple" ? t.purple : t.muted;
  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: t.surface2, border: "1px dashed " + t.borderStrong, borderRadius: 12, padding: "13px 15px", ...style }}>
      <Icon name={icon} size={16} color={col} style={{ marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        {label && <Eyebrow style={{ marginBottom: 4, color: col }}>{label}</Eyebrow>}
        <Body size={12.5}>{children}</Body>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ buttons & inputs
/** Pill button per SPEC §4: solid = accent fill with dark text; ghost = 1px strong border. */
export function Pill({ children, onClick, variant = "solid", size = "md", tone = "accent", icon, style = {}, disabled, title, type }: any) {
  const t = useT();
  const [h, setH] = React.useState(false);
  const pad = size === "sm" ? "6px 12px" : size === "lg" ? "13px 22px" : "9px 16px";
  const fs = size === "sm" ? 11 : 12.5;
  const toneColor = tone === "accent" ? t.accent : tone === "purple" ? t.purple : tone === "bad" ? t.bad : tone === "good" ? t.good : t.fg;
  let bg: string, fg: string, bd: string;
  if (variant === "solid") { bg = disabled ? t.surface2 : toneColor; fg = disabled ? t.muted : "#1a1033"; bd = "transparent"; }
  else if (variant === "soft") { bg = tone === "accent" ? t.accentSoft : tone === "purple" ? t.purpleSoft : tone === "bad" ? t.badSoft : t.surface2; fg = toneColor; bd = "transparent"; }
  else { bg = h ? t.surface2 : "transparent"; fg = t.fg2; bd = t.borderStrong; }
  return (
    <button type={type || "button"} onClick={disabled ? undefined : onClick} disabled={disabled} title={title}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: fs, letterSpacing: "0.05em", textTransform: "uppercase", background: bg, color: fg, border: "1px solid " + bd, borderRadius: 999, padding: pad, cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: disabled ? 0.6 : 1, filter: h && !disabled ? "brightness(1.08)" : "none", transition: "filter 140ms", whiteSpace: "nowrap", ...style }}>
      {icon && <Icon name={icon} size={fs + 3} />}{children}
    </button>
  );
}
export function LinkBtn({ children, onClick, icon = "arrowRight", style = {} }: any) {
  const t = useT();
  return (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: t.purple, ...style }}>
      {icon === "back" && <Icon name="chevron" size={12} color={t.purple} style={{ transform: "rotate(180deg)" }} />}
      {children}
      {icon && icon !== "back" && <Icon name={icon} size={12} color={t.purple} />}
    </button>
  );
}
export function Toggle({ on, onChange, label }: any) {
  const t = useT();
  return (
    <button type="button" onClick={() => onChange(!on)} aria-pressed={on} style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
      <span style={{ width: 38, height: 21, borderRadius: 999, background: on ? t.good : t.surface2, border: "1px solid " + (on ? "transparent" : t.borderStrong), position: "relative", transition: "background 160ms", flexShrink: 0 }}>
        <span style={{ position: "absolute", top: 2, left: on ? 19 : 2, width: 15, height: 15, borderRadius: 999, background: on ? "#14301f" : t.muted, transition: "left 160ms" }} />
      </span>
      {label && <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, textAlign: "left" }}>{label}</span>}
    </button>
  );
}
export function Tabs({ items, value, onChange, style = {} }: any) {
  const t = useT();
  return (
    <div role="tablist" style={{ display: "flex", gap: 4, flexWrap: "wrap", ...style }}>
      {items.map((it: any) => {
        const active = it.id === value;
        return (
          <button key={it.id} role="tab" aria-selected={active} onClick={() => onChange(it.id)}
            style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, letterSpacing: "0.05em", textTransform: "uppercase", padding: "8px 14px", borderRadius: 999, border: "1px solid " + (active ? "transparent" : it.tone === "bad" ? t.bad : t.border), background: active ? (it.tone === "bad" ? t.bad : t.purple) : "transparent", color: active ? "#1a1033" : t.muted, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
            {it.label}
            {it.count != null && <span style={{ background: active ? "rgba(26,16,51,0.18)" : t.surface2, borderRadius: 999, padding: "1px 6px", fontSize: 10.5 }}>{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
export function Modal({ title, children, onClose, footer, width = 520 }: any) {
  const t = useT();
  React.useEffect(() => {
    const on = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(6,4,16,0.72)", zIndex: 200, display: "grid", placeItems: "center", padding: 24 }}>
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: t.cardRadius, width: "100%", maxWidth: width, maxHeight: "86vh", overflow: "auto", padding: 22, boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <Title size={17}>{title}</Title>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}><Icon name="x" size={18} color={t.muted} /></button>
        </div>
        {children}
        {footer && <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 20, flexWrap: "wrap" }}>{footer}</div>}
      </div>
    </div>
  );
}
export const inputBase = (t: any): React.CSSProperties => ({
  width: "100%", background: t.appBg, border: "1px solid " + t.borderStrong, borderRadius: 10, padding: "10px 12px",
  fontFamily: FIGTREE, fontSize: 13.5, color: t.fg, outline: "none", boxSizing: "border-box", colorScheme: "dark",
});
export function TextArea({ value, onChange, placeholder, rows = 3 }: any) {
  const t = useT();
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{ ...inputBase(t), resize: "vertical" }} />;
}
export function PlainInput({ value, onChange, placeholder, type = "text", style = {}, maxLength }: any) {
  const t = useT();
  return <input type={type} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputBase(t), ...style }} />;
}
export function SearchInput({ value, onChange, placeholder, style = {}, inputRef, onKeyDown, onFocus }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, background: t.appBg, border: "1px solid " + t.border, borderRadius: 999, padding: "8px 14px", ...style }}>
      <Icon name="search" size={15} color={t.muted} />
      <input ref={inputRef} value={value} onKeyDown={onKeyDown} onFocus={onFocus} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        style={{ flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", fontFamily: FIGTREE, fontSize: 13.5, color: t.fg }} />
      {value && <button onClick={() => onChange("")} aria-label="Clear" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><Icon name="x" size={13} color={t.muted} /></button>}
    </div>
  );
}
export function LabelledField({ label, hint, counter, children }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg }}>{label}</span>
        {hint && <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{hint}</span>}
        {counter && <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: counter[0] > counter[1] ? t.bad : t.muted, marginLeft: "auto" }}>{counter[0]}/{counter[1]}</span>}
      </div>
      {children}
    </div>
  );
}
export function RangePicker({ value, onChange }: any) {
  const t = useT();
  const opts = [["7d", "7D", "Last 7 days"], ["30d", "30D", "Last 30 days"], ["90d", "90D", "Last 90 days"], ["all", "All", "All time"]];
  return (
    <div style={{ display: "inline-flex", background: t.surface2, border: "1px solid " + t.border, borderRadius: 999, padding: 2 }}>
      {opts.map(([id, label, long]) => (
        <button key={id} onClick={() => onChange(id)} title={long} aria-pressed={value === id}
          style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", padding: "5px 11px", borderRadius: 999, border: "none", cursor: "pointer", background: value === id ? t.purple : "transparent", color: value === id ? "#1a1033" : t.muted }}>
          {label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ numbers
/** Period-over-period direction. NOT a target comparison (SPEC §2). */
export function Trend({ delta, unit, goodWhenUp = true }: { delta: number | null | undefined; unit?: string; goodWhenUp?: boolean }) {
  const t = useT();
  if (delta == null || !Number.isFinite(delta)) return null;
  const flat = delta === 0;
  const up = delta > 0;
  const col = flat ? t.muted : (goodWhenUp ? up : !up) ? t.good : t.bad;
  return (
    <span title="Change since the previous period of the same length" style={{ display: "inline-flex", alignItems: "center", gap: 2, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: col, whiteSpace: "nowrap" }}>
      {flat ? <span style={{ width: 10, height: 2, background: col, borderRadius: 2 }} /> : <Icon name={up ? "arrowUp" : "arrowDown"} size={12} color={col} />}
      {flat ? "flat" : Math.abs(delta) + (unit === "%" ? " pts" : unit || "")}
    </span>
  );
}

/** A number with its plain-English meaning, and where it leads (SPEC §5.1 tile anatomy). */
export function AppStat({ label, value, unit, meaning, sub, icon, accent, onClick, tooFewToTrust, delta, deltaGood = true, drill = "See the people" }: any) {
  const t = useT();
  const col = accent || t.fg;
  const inner = (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <Eyebrow style={{ lineHeight: 1.3 }}>{label}</Eyebrow>
        {icon && <Icon name={icon} size={15} color={accent || t.muted} />}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 38, lineHeight: 1, color: col, fontVariantNumeric: "tabular-nums" }}>{value}</span>
          {unit && value !== "—" && <span style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 22, color: col }}>{unit}</span>}
        </span>
        <Trend delta={delta} unit={unit} goodWhenUp={deltaGood} />
      </div>
      {tooFewToTrust && <Badge tone="neutral" style={{ alignSelf: "flex-start" }}>Too few to trust</Badge>}
      {sub && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.4 }}>{sub}</div>}
      {meaning && <div style={{ marginTop: "auto", paddingTop: 7, fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.4, borderTop: "1px solid " + t.border }}>{meaning}</div>}
      {onClick && <div style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: t.purple }}>{drill}<Icon name="arrowRight" size={12} color={t.purple} /></div>}
    </>
  );
  return (
    <Card pad={17} onClick={onClick} hoverable={!!onClick} style={{ borderRadius: t.tileRadius, display: "flex", flexDirection: "column", gap: 7, minHeight: 132 }}>
      {inner}
    </Card>
  );
}

/** Horizontal labelled bars. */
export function Bars({ rows, max, showPct = true, height = 10, onRow }: any) {
  const t = useT();
  const top = max != null ? max : Math.max(...rows.map((r: any) => r.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {rows.map((r: any, i: number) => {
        const row = (
          <>
            <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2, display: "flex", alignItems: "center", gap: 7, minWidth: 0, textAlign: "left" }}>
              {r.color && <Dot color={r.color} />}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
            </div>
            <div style={{ background: "rgba(0,0,0,0.35)", borderRadius: 999, height, overflow: "hidden" }}>
              <div style={{ width: Math.max(r.value ? 2 : 0, (r.value / top) * 100) + "%", height: "100%", background: r.barColor || r.color || t.accent, opacity: r.faded ? 0.4 : 1, borderRadius: 999, transition: "width 400ms cubic-bezier(.2,.7,.2,1)" }} />
            </div>
            <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, whiteSpace: "nowrap", textAlign: "right", minWidth: 68, fontVariantNumeric: "tabular-nums" }}>
              {r.display ?? r.value}
              {showPct && r.pct != null && <span style={{ color: t.muted, fontWeight: 500 }}> · {r.pct}%</span>}
            </div>
          </>
        );
        const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "minmax(110px,1.1fr) 2.4fr auto", gap: 14, alignItems: "center" };
        return onRow ? (
          <button key={i} onClick={() => onRow(r)} style={{ ...grid, background: "none", border: "none", padding: 0, cursor: "pointer", width: "100%" }}>{row}</button>
        ) : (
          <div key={i} style={grid}>{row}</div>
        );
      })}
    </div>
  );
}

/** Vertical columns, value above, label below. */
export function Columns({ rows, height = 130, color }: any) {
  const t = useT();
  const top = Math.max(...rows.map((r: any) => r.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: height + 42 }}>
      {rows.map((r: any, i: number) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 0 }}>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: r.value ? t.muted : "transparent" }}>{r.value}</div>
          <div style={{ width: "100%", height: Math.max(r.value ? 3 : 1, (r.value / top) * height), background: r.value ? r.color || color || t.purple : t.border, borderRadius: 5 }} />
          <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.muted, textAlign: "center", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{r.label}</div>
        </div>
      ))}
    </div>
  );
}

/** Area + line, last-point dot, up to 5 x-ticks. */
export function Line({ data, color, height = 150, valueKey = "value", labelKey = "label" }: any) {
  const t = useT();
  const c = color || t.accent;
  const n = data.length;
  const gid = React.useId().replace(/:/g, "");
  if (!n) return null;
  const vals = data.map((d: any) => Number(d[valueKey]) || 0);
  const max = Math.max(...vals, 1);
  const W = 600, H = height;
  const fx = (i: number) => (n <= 1 ? 0.5 : i / (n - 1));
  const X = (i: number) => fx(i) * W;
  const Y = (v: number) => (1 - v / max) * (H - 8) + 4;
  const line = vals.map((v: number, i: number) => (i ? "L" : "M") + X(i).toFixed(1) + " " + Y(v).toFixed(1)).join(" ");
  const area = line + ` L${X(n - 1).toFixed(1)} ${H} L${X(0).toFixed(1)} ${H} Z`;
  const k = Math.min(5, n);
  const ticks = n <= 1 ? [0] : [...new Set(Array.from({ length: k }, (_, j) => Math.round((j * (n - 1)) / Math.max(1, k - 1))))];
  return (
    <div style={{ width: "100%" }}>
      <div style={{ position: "relative", width: "100%", height: H }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H, display: "block", overflow: "visible" }}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={c} stopOpacity="0.28" /><stop offset="1" stopColor={c} stopOpacity="0" /></linearGradient></defs>
          <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke={t.border} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d={area} fill={`url(#${gid})`} />
          <path d={line} fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <span style={{ position: "absolute", width: 8, height: 8, borderRadius: 999, background: c, transform: "translate(-50%,-50%)", left: fx(n - 1) * 100 + "%", top: (Y(vals[n - 1]) / H) * 100 + "%" }} />
      </div>
      <div style={{ position: "relative", height: 16, marginTop: 6 }}>
        {ticks.map((ti) => (
          <span key={ti} style={{ position: "absolute", left: fx(ti) * 100 + "%", transform: `translateX(${ti === 0 ? "0" : ti === n - 1 ? "-100%" : "-50%"})`, fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, whiteSpace: "nowrap" }}>{data[ti][labelKey]}</span>
        ))}
      </div>
    </div>
  );
}

/** Steps with the drop between each named, the biggest leak flagged. */
export function Funnel({ steps, biggestLeak }: { steps: { label: string; value: number }[]; biggestLeak: number | null }) {
  const t = useT();
  const top = steps[0]?.value || 0;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {steps.map((s, i) => {
        const prev = i ? steps[i - 1].value : null;
        const drop = prev != null ? prev - s.value : 0;
        const isLeak = biggestLeak === i;
        return (
          <div key={i}>
            {i > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0 5px 14px", marginLeft: 6, borderLeft: "2px dashed " + (isLeak ? t.bad : t.border) }}>
                <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: isLeak ? t.bad : t.muted }}>{drop > 0 ? `${drop} fell out here` : drop < 0 ? "more reached this step (some started earlier)" : "none lost"}</span>
                {isLeak && <Badge tone="bad">Biggest leak</Badge>}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(140px,1.2fr) 2.2fr auto", gap: 14, alignItems: "center" }}>
              <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2 }}>{s.label}</div>
              <div style={{ background: "rgba(0,0,0,0.35)", borderRadius: 999, height: 12, overflow: "hidden" }}>
                <div style={{ width: (top ? Math.max(s.value ? 2 : 0, Math.min(100, (s.value / top) * 100)) : 0) + "%", height: "100%", background: isLeak ? t.bad : i === 0 ? t.purple : t.accent, borderRadius: 999 }} />
              </div>
              <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, textAlign: "right", minWidth: 74, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                {s.value}{top ? <span style={{ color: t.muted, fontWeight: 500 }}> · {Math.round((s.value / top) * 100)}%</span> : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ calendar
export const DAY_STATE: Record<string, { color: string; label: string; ink: "dark" | "light" }> = {
  taken: { color: "#6fe3a0", label: "Taken", ink: "dark" },
  missed: { color: "#ff8b7d", label: "Missed", ink: "dark" },
  unanswered: { color: "rgba(185,169,255,0.22)", label: "Not answered", ink: "light" },
  future: { color: "rgba(255,255,255,0.06)", label: "Still to come", ink: "light" },
};
export function CalendarStrip({ days, onPick, selected }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
      {days.map((d: any) => {
        const st = DAY_STATE[d.state] || DAY_STATE.future;
        const sel = selected === d.day;
        return (
          <button key={d.day} onClick={() => onPick && onPick(d)} aria-pressed={sel}
            title={`Day ${d.day} · ${st.label}${d.binder ? " · binder day" : ""}`}
            style={{ width: 34, height: 40, borderRadius: 8, background: st.color, border: "1px solid " + (sel ? t.fg : "transparent"), cursor: onPick ? "pointer" : "default", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, padding: 0 }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: st.ink === "dark" ? "#1a1033" : t.muted }}>{d.day}</span>
            {d.binder && <Dot color={d.binderState === "missed" ? BINDER_COLOR : st.ink === "dark" ? "rgba(26,16,51,0.55)" : t.fg2} size={5} />}
          </button>
        );
      })}
    </div>
  );
}
export function CalendarLegend() {
  const t = useT();
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 12 }}>
      {Object.keys(DAY_STATE).map((k) => (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>
          <span style={{ width: 12, height: 12, borderRadius: 4, background: DAY_STATE[k].color }} />{DAY_STATE[k].label}
        </span>
      ))}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}><Dot color={t.fg2} size={6} />Dot = binder day</span>
    </div>
  );
}

export function Timeline({ events }: { events: { when: string; text: string; detail?: string; by?: string; color?: string }[] }) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {events.map((e, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "92px 20px 1fr", gap: 10, alignItems: "start" }}>
          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, paddingTop: 11, textAlign: "right" }}>{e.when}</div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "stretch" }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: e.color || t.purple, marginTop: 13, flexShrink: 0 }} />
            {i < events.length - 1 && <span style={{ width: 1, flex: 1, minHeight: 20, background: t.border }} />}
          </div>
          <div style={{ padding: "8px 0 14px", minWidth: 0 }}>
            <div style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{e.text}</div>
            {e.detail && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 2 }}>{e.detail}</div>}
            {e.by && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginTop: 3 }}>{e.by}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Skeleton tiles for a loading overview. */
export function SkeletonTiles({ n = 6 }: { n?: number }) {
  const t = useT();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))", gap: 14 }}>
      {Array.from({ length: n }, (_, i) => (
        <Card key={i} style={{ borderRadius: t.tileRadius, minHeight: 132, opacity: 0.55 }}>
          <div style={{ width: "52%", height: 10, background: t.surface2, borderRadius: 999 }} />
          <div style={{ width: "34%", height: 30, background: t.surface2, borderRadius: 8, marginTop: 16 }} />
          <div style={{ width: "78%", height: 9, background: t.surface2, borderRadius: 999, marginTop: 18 }} />
        </Card>
      ))}
    </div>
  );
}

/** A small transient confirmation at the bottom of the screen. */
export function useToast() {
  const [msg, setMsg] = React.useState<{ text: string; bad?: boolean } | null>(null);
  React.useEffect(() => {
    if (!msg) return;
    const id = setTimeout(() => setMsg(null), 3400);
    return () => clearTimeout(id);
  }, [msg]);
  const node = msg ? (
    <div role="status" style={{ position: "fixed", bottom: 22, left: "50%", transform: "translateX(-50%)", background: msg.bad ? "#ff8b7d" : "#6fe3a0", color: msg.bad ? "#2a0f0c" : "#14301f", fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, padding: "12px 20px", borderRadius: 999, zIndex: 300, boxShadow: "0 12px 30px rgba(0,0,0,0.45)", maxWidth: "90vw" }}>{msg.text}</div>
  ) : null;
  return { toast: (text: string, bad?: boolean) => setMsg({ text, bad }), toastNode: node };
}
